<template>
  <NewAppointmentDialog v-model="showAppointmentDialog"
                        :customer="customer"
                        @onOpen="() => {showAppointmentDialog = false}"
                        @onSubmit="onAppointmentCreated"
                        :appointment="examination.appointment"/>
  <el-dialog :width="isSmallScreen ? '92%' : '30%'" v-model="selectCustomerDialogVisiable"
             :title="t('selectCustomer')">
    <div class="flex items-center justify-center" :dir="$ctx.getDir()">
      <el-select
          ref="customerSelect"
          filterable
          clearable
          class="w-full"
          value-key="contactDisplayName"
          v-model="selectedCustomerElSelect"
          remote
          :remote-method="searchCustomers"
      >
        <el-option
            v-for="option in customers"
            :key="option.id"
            :label="option.contactDisplayName"
            :value="option"
        >
          <span style="float: left">{{ option.contactDisplayName }}</span>
          <span
            v-if="option.phone"
            style="
              float: right;
              color: var(--el-text-color-secondary);
              font-size: 13px;
            "
          >
            {{ option.phone }}
          </span>
        </el-option>
        <template #footer>
          <button v-if="customerSearchTerm" type="button" class="add-typed-customer" :dir="$ctx.getDir()" @click="openNewCustomer">
            <span class="fa fa-plus"></span>
            <bdi>{{ t('addAsNewCustomer', {name: customerSearchTerm}) }}</bdi>
          </button>
        </template>
      </el-select>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button
            @click="openNewCustomer"
            type="secondary"
        ><span class="fa fa-plus"></span> {{ t('views.customers.newCustomer') }}</el-button
        >
        <el-button
            :disabled="!selectedCustomerElSelect"
            @click="confirmSelectCustomer()"
            type="primary"
        >{{ t('confirm') }}</el-button
        >
      </span>
    </template>
  </el-dialog>
  <el-dialog v-model="newCustomerDialogVisiable"
             :width="isSmallScreen ? '95%' : undefined"
             :title="t('views.customers.newCustomer')">
    <NewCustomerDialog v-if="examination.customer && newCustomerDialogVisiable" @success="onCustomerCreater" v-model="examination.customer" :initName="customerSearchTerm"></NewCustomerDialog>
    <NewCustomerDialog v-else @success="onCustomerCreater" :initName="customerSearchTerm"></NewCustomerDialog>
  </el-dialog>
  <el-form
      @submit.prevent="validateSubmit('examinationForm')"
      :class="{ ar: locale == 'ar' }"
      ref="examinationForm"
      :model="examination"
      :rules="rules"
      label-position="top"
      class="m-8 bg-white shadow-md p-4 gap-1 examination-form"
      style="direction: ltr"
      novalidate
      :disabled="premiumLocked"
  >
    <div v-if="premiumLocked" class="premium-banner" role="status" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
      <span class="premium-banner-icon" aria-hidden="true"><Icon icon="mdi:crown" width="22" height="22"/></span>
      <span class="flex flex-col min-w-0">
        <span class="premium-banner-title">{{ t('premiumTitle') }}</span>
        <span class="premium-banner-body">{{ t('premiumBody') }}</span>
      </span>
      <span class="premium-banner-lock" aria-hidden="true"><Icon icon="mdi:lock" width="16" height="16"/></span>
    </div>
    <div class="premium-content" :class="{ 'premium-locked': premiumLocked }" :inert="premiumLocked"
         :aria-disabled="premiumLocked || undefined">
    <el-tabs v-model="activeTab" class="w-full" type="card" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
      <el-tab-pane name="exam" :label="isRetinoscopy ? t('tabRetinoscopy') : t('tabEyeExam')" />
      <el-tab-pane name="attachments" :label="`${t('tabAttachments')} (${attachmentsCount})`" />
      <el-tab-pane name="notes" :label="`${t('tabInternalNotes')} (${notesCount})`" />
    </el-tabs>

    <!-- Eye Exam tab -->
    <div v-show="activeTab === 'exam'" class="w-full">
    <div class="exam-layout">
      <!-- Customer -->
      <div class="exam-card patient-card" :dir="locale == 'ar' ? 'rtl' : 'ltr'">
        <div class="patient-row">
          <div v-if="customer.id" class="patient-identity">
            <div class="patient-avatar" aria-hidden="true"><Icon icon="mdi:account" width="28" height="28"/></div>
            <div class="patient-main">
              <span class="patient-label">{{ t('name') }}</span>
              <div class="patient-name-row">
                <button type="button" tabindex="1" class="patient-name" dir="auto" :title="customerDisplayName"
                        @click="newCustomerDialogVisiable = true">{{ customerDisplayName }}</button>
                <button type="button" class="patient-clear" :title="t('changeCustomer')" :aria-label="t('changeCustomer')"
                        @click="clearCustomer">
                  <Icon icon="mdi:close" width="16" height="16"/>
                </button>
              </div>
              <span v-if="customer.phone" class="patient-phone"><bdi>{{ customer.phone }}</bdi></span>
            </div>
          </div>
          <button v-else type="button" tabindex="1" autofocus class="patient-empty"
                  @click="selectCustomerDialogVisiable = true">
            <span class="patient-empty-icon"><Icon icon="mdi:account-plus" width="20" height="20"/></span>
            <span class="flex flex-col items-start">
              <span class="font-bold">{{ t('selectCustomer') }}</span>
              <span class="text-xs text-gray-500">{{ t('selectCustomerHint') }}</span>
            </span>
          </button>

          <div class="patient-stats">
            <button type="button" tabindex="-1" class="patient-stat" :disabled="!customer.id"
                    @click="newCustomerDialogVisiable = true">
              <span class="patient-label">{{ t('dateOfBirth') }}</span>
              <span class="patient-stat-value" dir="ltr">{{ customerDateOfBirth || '—' }}</span>
            </button>
            <div class="patient-stat">
              <span class="patient-label">{{ t('age') }}</span>
              <span class="patient-stat-value" dir="auto">{{ customerAge || '—' }}</span>
            </div>
            <div v-if="examinationCreatedDate && examination.externalID" class="patient-stat">
              <span class="patient-label">{{ t('examinationNumber') }}</span>
              <span class="patient-stat-value" dir="ltr">{{ examination.externalID }}</span>
            </div>
            <div v-if="examinationCreatedDate" class="patient-stat">
              <span class="patient-label">{{ t('examinationDate') }}</span>
              <span class="patient-stat-value" dir="ltr">{{ examinationCreatedDate }}</span>
            </div>
          </div>

          <div class="patient-tools">
            <CustomerOldExaminationDrawer v-if="customer.id" :customer="customer" :currentExaminationId="examination.id" @examinationSelected="onExaminationSelected" />
            <QueueShortcutDrawer v-if="useSettingsStore().getValue('show_queue_button_on_customers_page') === 'true'" @addCustomer="addCustomer"/>
          </div>
        </div>
      </div>

      <EyeExamFields
          v-if="!isRetinoscopy"
          :examination="examination"
          :examinationOptions="examinationOptions"
          :prismOptions="prismOptions"
      />

      <!-- Notes -->
      <div class="exam-card">
        <div class="exam-card-title">{{ t('notes') }}</div>
        <el-input
            v-model="examination.notes"
            :autosize="{ minRows: 3, maxRows: 10 }"
            type="textarea"
            placeholder=""
        />
      </div>

      <!-- Retinoscopy: closed until opened, like Contact Lenses and Prism; shares the customer and footer of the exam -->
      <section class="exam-card" dir="ltr">
        <button type="button" class="retino-toggle" :disabled="retinoscopyPinned" @click="retinoscopyExpanded = !retinoscopyExpanded">
          <div class="retino-heading">
            <div class="font-bold text-base">Retinoscopy <bdi class="retino-abbr">RET</bdi></div>
            <div class="retino-subtitle"><bdi>{{ t('retinoscopySubtitle') }}</bdi></div>
          </div>
          <Icon v-if="retinoscopyLocked" icon="mdi:lock" width="18" class="retino-lock"/>
          <Icon v-if="!retinoscopyPinned" :icon="retinoscopyOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" width="22"/>
        </button>
        <div v-if="retinoscopyOpen" class="mt-4">
          <div v-if="retinoscopyLocked" class="retino-locked-note" :dir="locale === 'ar' ? 'rtl' : 'ltr'">{{ t('premiumBody') }}</div>
          <div :inert="retinoscopyLocked" :class="{ 'retino-locked': retinoscopyLocked }">
            <RetinoscopyFields v-model="examination.retinoscopy" :showNotes="!isRetinoscopy"/>
          </div>
        </div>
      </section>

      <!-- Optometrist / doctor / insurer / next appointment -->
      <div class="exam-card" :dir="locale == 'ar' ? 'rtl' : 'ltr'">
        <div class="exam-card-title">{{ t('careTeam') }}</div>
        <div class="care-grid">
          <div class="care-field">
            <span class="care-label custom-required">{{ t('optometrist') }}</span>
            <el-select
                filterable
                clearable
                v-model="examination.examinatorID"
                class="w-full examination-input"
                :placeholder="t('chooseOptometrist')"
            >
              <el-option
                  v-for="option in examinators"
                  :key="option.id"
                  :label="`${option.name ? option.name : ''}`"
                  :value="option.id"
              ></el-option>
            </el-select>
          </div>
          <div class="care-field">
            <span class="care-label">{{ t('ophthalmologist') }}</span>
            <el-select
                filterable
                clearable
                v-model="examination.doctor"
                class="w-full examination-input"
                :placeholder="t('chooseOphthalmologist')"
            >
              <el-option
                  v-for="option in ophthalmologists"
                  :key="option"
                  :label="option"
                  :value="option"
              ></el-option>
            </el-select>
          </div>
          <div v-if="useSettingsStore().getValue('show_insurance_companies_button_on_companies_page') == 'true'" class="care-field">
            <span class="care-label">{{ t('insuranceCompany') }}</span>
            <el-select
                filterable
                clearable
                v-model="examination.insuranceID"
                class="w-full examination-input"
                :placeholder="t('chooseInsurer')"
            >
              <el-option
                  v-for="ins in insurances"
                  :key="ins.id"
                  :label="ins.name"
                  :value="ins.id"
              ></el-option>
            </el-select>
          </div>
          <div class="care-field">
            <span class="care-label">{{ t('nextAppointment') }}</span>
            <button type="button"
                    class="care-appt"
                    :class="{ 'care-appt-set': examination.nextAppointment }"
                    @click="() => {showAppointmentDialog = true}">
              <Icon :icon="examination.nextAppointment ? 'mdi:calendar-check' : 'mdi:calendar-plus'" width="18" height="18"/>
              <span dir="auto">{{ examination.nextAppointment ? formatDate(examination.nextAppointment) : t('createAppointment') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="w-full flex flex-col input-section">
      <div class="flex flex-col md:flex-row items-center justify-end">
        <div class="md:w-4/12 m-2"
             v-if="enablePrint && !isRetinoscopy && useSettingsStore().getValue('hide_examination_id_print') == 'false'">
          <horizontal-button
              :title="t('printID')"
              faIcon="credit-card"
              is-warning
              @click.prevent="printID()"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enablePrint">
          <horizontal-button :title="$t('actions.print')" faIcon="print"
                             @click.prevent="print()"/>
        </div>

        <div class="md:w-4/12 m-2"
             v-if="enablePrint && !isRetinoscopy && useSettingsStore().getValue('show_qr_codes_on_eye_examination_reports') == 'true'">
          <horizontal-button
              :title="t('saveQRCode')"
              @click.prevent="downloadQR()"
          >
            <Icon icon="mdi:qrcode" width="18" height="18" />
          </horizontal-button>
        </div>

        <div class="md:w-4/12 m-2" v-if="!examination.id">
          <horizontal-button
              :title="t('saveAndPrint')"
              @click.prevent="validateSubmit('examinationForm', true)"
              :disabled="isSubmitting"
          >
            <span class="flex gap-1">
              <font-awesome-icon icon="save"/>
              <font-awesome-icon icon="print"/>
            </span>
          </horizontal-button>
        </div>

        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.save')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('examinationForm')"
              :disabled="isSubmitting"
              :loading="isSubmitting"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('examinationForm')"
          />
        </div>
      </div>
    </div>
    </div>
    <!-- end Eye Exam tab -->

    <!-- Attachments tab -->
    <div v-show="activeTab === 'attachments'" class="w-full mt-4">
      <ExaminationAttachmentsManager :data="examination" v-model="pendingAttachments" />
    </div>
    <!-- end Attachments tab -->

    <!-- Internal Notes tab -->
    <div v-show="activeTab === 'notes'" class="w-full mt-4 px-2">
      <!-- Subtitle -->
      <p class="text-sm font-bold text-blue-700 mb-4">{{ t('notesSubtitle') }}</p>

      <!-- Compose area at top -->
      <el-input
        v-model="newNote"
        type="textarea"
        :rows="5"
        :placeholder="t('writeNote')"
        class="w-full mb-3"
      />
      <div class="flex gap-2 mb-6">
        <el-button
          type="primary"
          style="background-color:#1e3a5f;border-color:#1e3a5f;padding:10px 24px;font-weight:600"
          :disabled="!newNote.trim() || !examination.id"
          @click="openPinDialog"
        >{{ t('save') }}</el-button>
        <el-button style="padding:10px 24px" @click="newNote = ''">{{ t('cancel') }}</el-button>
      </div>
      <p v-if="!examination.id" class="text-xs text-gray-400 -mt-4 mb-4">{{ t('saveExamFirst') }}</p>

      <!-- Notes list as cards -->
      <div class="space-y-3">
        <div
          v-for="note in examinationNotes"
          :key="note.id"
          class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
        >
          <!-- Card header -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <div class="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img v-if="note.user?.avatar" :src="storagePath + '/' + note.user.avatar" class="w-full h-full object-cover" />
                <Icon v-else icon="mdi:account" class="w-5 h-5 text-gray-500" />
              </div>
              <span class="text-sm font-semibold text-blue-600">{{ note.doctorName || note.user?.name || '' }}</span>
              <span
                v-if="note.doctorName"
                class="text-[11px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5"
              >{{ t('external') }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-400">{{ formatNoteDateTime(note.createdAt) }}</span>
              <button
                v-if="note.user?.id === currentUserID"
                type="button"
                class="text-gray-400 hover:text-blue-500 p-0.5"
                @click="startEditNote(note)"
              >
                <Icon icon="mdi:pencil-box-outline" class="w-4 h-4" />
              </button>
            </div>
          </div>
          <!-- Card body -->
          <template v-if="editingNoteID === note.id">
            <el-input v-model="editingNoteText" type="textarea" :rows="3" class="mb-2" />
            <div class="flex gap-2">
              <el-button size="small" type="primary" @click="saveNoteEdit(note.id)">{{ t('save') }}</el-button>
              <el-button size="small" @click="cancelEdit">{{ t('cancel') }}</el-button>
            </div>
          </template>
          <p v-else class="text-sm text-gray-700 whitespace-pre-wrap">{{ note.note }}</p>
        </div>
        <div v-if="examinationNotes.length === 0" class="text-sm text-gray-400 text-center py-4">
          {{ t('noNotes') }}
        </div>
      </div>
    </div>
    <!-- end Internal Notes tab -->

    <!-- PIN dialog for internal notes -->
    <el-dialog v-model="notePinDialogOpen" :title="t('enterPin')" width="300px" append-to-body @opened="$refs.notePinInput?.focus()">
      <el-input
        ref="notePinInput"
        v-model="notePin"
        type="password"
        :placeholder="t('enterPin')"
        @keyup.enter="notePin.trim() && confirmPinAndSubmit()"
      />
      <template #footer>
        <el-button @click="notePinDialogOpen = false; notePin = ''">{{ t('cancel') }}</el-button>
        <el-button type="primary" :disabled="!notePin.trim()" @click="confirmPinAndSubmit">{{ t('confirm') }}</el-button>
      </template>
    </el-dialog>
    </div>

  </el-form>
</template>

<script lang="ts">
/* eslint-disable */

import {defineComponent} from 'vue'
import {useMediaQuery} from '@vueuse/core'
import {ElForm, ElMessage} from 'element-plus'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// components
import HorizontalButton from '@/components/HorizontalButton.vue'

// models
import Examination, {
  EXAM_TYPE_EYE_EXAM,
  EXAM_TYPE_RETINOSCOPY,
  ExaminationNote,
  emptyRetinoscopy,
  examinationI18nMessages,
  normalizeRetinoscopy,
  retinoscopyIsFilled,
  retinoscopyMissingWorkingDistance
} from '@/models/customer/Examination'
import Customer, {customerI18nMessages} from '@/models/customer/Customer'
import {useI18n} from 'vue-i18n'

import {formatAge, formatDate, formatPostgresDate} from '@/utils/date'
import NewCustomerDialog from '@/dialogs/customer/NewCustomerDialog.vue'
import User from '@/models/user/User'
import {Icon} from '@iconify/vue'
import {useSettingsStore} from "@/stores/settingsStore";
import NewAppointmentDialog
  from "@/views/appointments/NewAppointmentDialog.vue";
import Appointment from "@/models/customer/Appointment";
import NewVendorDialog from "@/views/companies/vendors/NewVendorDialog.vue";
import Insurance from "@/models/insurance/Insurance";
import CustomNumberInput from "@/views/examinations/CustomNumberInput.vue";
import RetinoscopyFields from "@/views/examinations/RetinoscopyFields.vue";
import EyeExamFields from "@/views/examinations/EyeExamFields.vue";
import CustomerOldExaminationDrawer from './CustomerOldExaminationDrawer.vue'
import QueueShortcutDrawer from './QueueShortcutDrawer.vue'
import ExaminationAttachmentsManager from '@/components/file-manager/ExaminationAttachmentsManager.vue'
import {exportQRCode} from '@/utils/qrExport'
import samiLogo from '@/assets/images/logo.svg'

export default defineComponent({
  name: 'examination-form',

  setup() {
    const {t, locale} = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...customerI18nMessages.en,
          ...examinationI18nMessages.en,
          age: 'Age',
          customerInfo: 'Customer Info',
          notes: 'Notes',
          pleaseSelectACustomer: 'Please select a Customer',
          pleaseSelectAnOptometrist: 'Please select an Optometrist',
          confirm: 'Confirm',
          printID: 'ID Print',
          saveAndPrint: 'Save & Print',
          saveQRCode: 'Save QR Code',
          ophthalmologist: 'Ophthalmologist',
          insuranceCompany: 'Insurer',
          month: 'Month',
          createAppointment: "Create Appointment",
          tabEyeExam: 'Eye Exam',
          tabRetinoscopy: 'Retinoscopy',
          retinoscopySubtitle: 'Optical cross for dry and cycloplegic retinoscopy',
          premiumTitle: 'Premium feature',
          premiumBody: 'Retinoscopy is a premium feature. Contact support to activate it.',
          workingDistanceRequired: 'Choose the Retinoscopy working distance (WD) for the Gross values entered',
          tabAttachments: 'Attachments',
          tabInternalNotes: 'Internal Notes',
          noNotes: 'No internal notes yet',
          writeNote: 'Write an internal note...',
          send: 'Send',
          save: 'Save',
          cancel: 'Cancel',
          saveExamFirst: 'Save the examination first to add notes',
          notesSubtitle: 'These notes will be seen by the optometrist and ophthalmologist',
          enterPin: 'Enter cashier pin',
          external: 'External',
          selectCustomer: 'Select a customer',
          selectCustomerHint: 'Search by name or phone, or add a new one',
          addAsNewCustomer: 'Add "{name}" as a new customer',
          changeCustomer: 'Change customer',
          careTeam: 'Examiner & Follow-up',
          chooseOptometrist: 'Choose optometrist',
          chooseOphthalmologist: 'Choose ophthalmologist',
          chooseInsurer: 'Choose insurer'
        },
        ar: {
          ...customerI18nMessages.ar,
          ...examinationI18nMessages.ar,
          age: 'العمر',
          customerInfo: 'بيانات الزبون',
          notes: 'الملاحظات',
          pleaseSelectACustomer: 'يرجى اختيار زبون',
          pleaseSelectAnOptometrist: 'يرجى اختيار الفاحص',
          confirm: 'اختيار',
          printID: 'طباعة الكرت',
          saveAndPrint: 'حفظ وطباعة',
          saveQRCode: 'حفظ رمز QR',
          ophthalmologist: 'طبيب العيون',
          insuranceCompany: 'شركة التأمين',
          month: 'شهر',
          createAppointment: "انشاء موعد",
          tabEyeExam: 'فحص العيون',
          tabRetinoscopy: 'Retinoscopy',
          retinoscopySubtitle: 'الفحص بدون القطرة، وبعد القطرة',
          premiumTitle: 'ميزة مدفوعة',
          premiumBody: 'فحص Retinoscopy ميزة مدفوعة. تواصل مع الدعم الفني لتفعيلها.',
          workingDistanceRequired: 'اختر مسافة العمل في فحص Retinoscopy',
          tabAttachments: 'المرفقات',
          tabInternalNotes: 'ملاحظات داخلية',
          noNotes: 'لا توجد ملاحظات داخلية',
          writeNote: 'اكتب ملاحظة داخلية...',
          send: 'إرسال',
          save: 'حفظ',
          cancel: 'إلغاء',
          saveExamFirst: 'احفظ الفحص أولاً لإضافة الملاحظات',
          notesSubtitle: 'ستُعرض هذه الملاحظات على الفاحص والطبيب',
          enterPin: 'أدخل رقم البصمة',
          external: 'خارجي',
          selectCustomer: 'اختر زبوناً',
          selectCustomerHint: 'ابحث بالاسم أو رقم الهاتف، أو أضف زبوناً جديداً',
          addAsNewCustomer: 'إضافة "{name}" كزبون جديد',
          changeCustomer: 'تغيير الزبون',
          careTeam: 'الفاحص والمتابعة',
          chooseOptometrist: 'اختر الفاحص',
          chooseOphthalmologist: 'اختر طبيب العيون',
          chooseInsurer: 'اختر شركة التأمين'
        }
      }
    })

    const rules = {
      id: [{required: true, message: t('validation.required')}]
    }

    const isSmallScreen = useMediaQuery('(max-width: 767px)')
    return {t, locale, rules, useSettingsStore, isSmallScreen}
  },

  components: {
    Icon,
    CustomNumberInput,
    RetinoscopyFields,
    EyeExamFields,
    NewVendorDialog,
    NewAppointmentDialog,
    HorizontalButton,
    NewCustomerDialog,
    CustomerOldExaminationDrawer,
    QueueShortcutDrawer,
    ExaminationAttachmentsManager
  },
  props: {
    initCustomer: {
      type: Customer,
      required: true
    },
    initExamination: {
      type: Examination,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    },
    enablePrint: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      examination: this.initExamination,
      examinators: [] as User[],
      ophthalmologists: [] as string[],
      insurances: [] as Insurance[],
      customer: this.initCustomer,
      customers: null as Customer[] | any,
      error: {title: '', body: ''},
      selectCustomerDialogVisiable: false,
      newCustomerDialogVisiable: false,
      selectedCustomerElSelect: null,
      customerSearchTerm: '',
      examinationOptions: [
        'No.LP',
        'L.P',
        'H.M',
        'C.F 0.5m',
        'C.F 1m',
        'C.F 2m',
        'C.F 3m',
        'C.F 4m',
        'C.F 5m',
        '6/60p',
        '6/60',
        '6/36p',
        '6/36',
        '6/24p',
        '6/24',
        '6/18p',
        '6/18',
        '6/12p',
        '6/12',
        '6/9p',
        '6/9',
        '6/6p',
        '6/6',
        '6/5',
        '6/4'
      ],
      prismOptions: [
        'B.IN',
        'B.OUT',
        'B.UP',
        'B.DOWN',
      ],
      showAppointmentDialog: false,
      isSubmitting: false,
      pendingAttachments: [] as File[],
      activeTab: 'exam',
      examinationNotes: [] as ExaminationNote[],
      newNote: '',
      editingNoteID: '',
      editingNoteText: '',
      notePinDialogOpen: false,
      notePin: '',
      retinoscopyExpanded: false
    }
  },
  beforeMount() {
    this.examination.retinoscopy = normalizeRetinoscopy(this.examination.retinoscopy)

    if (this.examination.readingODIPD == null) {
      delete this.examination.readingODIPD
    }

    if (this.examination.distanceODIPD == null) {
      delete this.examination.distanceODIPD
    }

    if (this.examination.contactODAXIS == null) {
      delete this.examination.contactODAXIS
    }

    if (this.examination.contactOSAXIS == null) {
      delete this.examination.contactOSAXIS
    }

    if (this.examination.distanceODAXIS == null) {
      delete this.examination.distanceODAXIS
    }

    if (this.examination.distanceOSAXIS == null) {
      delete this.examination.distanceOSAXIS
    }

    if (this.examination.readingODCYL == null) {
      delete this.examination.readingODCYL
    }

    if (this.examination.readingODAXIS == null) {
      delete this.examination.readingODAXIS
    }

    if (this.examination.readingOSAXIS == null) {
      delete this.examination.readingOSAXIS
    }

    if (this.examination.iopOD == null) {
      delete this.examination.iopOD
    }

    if (this.examination.iopOS == null) {
      delete this.examination.iopOS
    }

    if (this.examination.contactODK1 == null) {
      delete this.examination.contactODK1
    }
    if (this.examination.contactOSK1 == null) {
      delete this.examination.contactOSK1
    }

    if (this.examination.contactODK2 == null) {
      delete this.examination.contactODK2
    }
    if (this.examination.contactOSK2 == null) {
      delete this.examination.contactOSK2
    }

    if (this.examination.contactODKAVE == null) {
      delete this.examination.contactODKAVE
    }
    if (this.examination.contactOSKAVE == null) {
      delete this.examination.contactOSKAVE
    }

    if (this.examination.contactODBC == null) {
      delete this.examination.contactODBC
    }
    if (this.examination.contactOSBC == null) {
      delete this.examination.contactOSBC
    }

    if (this.examination.contactODDIA == null) {
      delete this.examination.contactODDIA
    }
    if (this.examination.contactOSDIA == null) {
      delete this.examination.contactOSDIA
    }

    if (this.examination.prismOD == null) {
      delete this.examination.prismOD
    }
    if (this.examination.prismOS == null) {
      delete this.examination.prismOS
    }

    if (this.examination.prismODBASE == null) {
      delete this.examination.prismODBASE
    }
    if (this.examination.prismOSBASE == null) {
      delete this.examination.prismOSBASE
    }

    if (this.examination.prismODAXIS == null) {
      delete this.examination.prismODAXIS
    }
    if (this.examination.prismOSAXIS == null) {
      delete this.examination.prismOSAXIS
    }

  },
  async mounted() {
    if (!this.customer.id) {
      const url = `${Customer.ENDPOINT}`
      const customersRes = await this.$http.get<Customer[]>(url)
    }

    const usersRes = await this.$http.get<User[]>(User.ENDPOINT + '/optometrists')
    this.examinators = usersRes.map((user) => User.from(user))

    const insurancesRes = await this.$http.get<Insurance[]>(Insurance.ENDPOINT)
    this.insurances = insurancesRes.map((ins) => Insurance.from(ins))

    // Load ophthalmologists from settings
    const settingsStore = useSettingsStore()
    const ophthalmologistsValue = settingsStore.getValue('ophthalmologists')
    if (ophthalmologistsValue) {
      try {
        this.ophthalmologists = typeof ophthalmologistsValue === 'string'
          ? JSON.parse(ophthalmologistsValue)
          : ophthalmologistsValue
      } catch (e) {
        console.error('Error parsing ophthalmologists:', e)
        this.ophthalmologists = []
      }
    }
  },
  watch: {
    'initExamination.id': {
      immediate: true,
      handler(newId: string) {
        if (newId) this.fetchNotes()
      }
    }
  },
  computed: {
    // Old stand-alone Retinoscopy exam (saved before Retinoscopy moved into the Eye Exam page)
    isRetinoscopy(): boolean {
      return this.examination.examType === EXAM_TYPE_RETINOSCOPY
    },
    // Secret Settings flag (premium feature)
    retinoscopyEnabled(): boolean {
      return useSettingsStore().getValue('enable_retinoscopy_exam') === 'true'
    },
    // A stand-alone Retinoscopy exam is shown read-only while the feature is switched off
    premiumLocked(): boolean {
      return this.isRetinoscopy && !this.retinoscopyEnabled
    },
    hasRetinoscopyData(): boolean {
      return retinoscopyIsFilled(this.examination.retinoscopy)
    },
    // A saved Retinoscopy stays readable after the feature is switched off, but can't be edited
    retinoscopyLocked(): boolean {
      return !this.retinoscopyEnabled && !this.isRetinoscopy
    },
    // Like Contact Lenses and Prism: a section with values always stays open
    retinoscopyPinned(): boolean {
      return this.isRetinoscopy || this.hasRetinoscopyData
    },
    retinoscopyOpen(): boolean {
      return this.retinoscopyPinned || this.retinoscopyExpanded
    },
    currentUserID(): string {
      try { return JSON.parse(localStorage.getItem('user') || '{}').id || '' } catch { return '' }
    },
    storagePath(): string {
      return useSettingsStore().storagePath
    },
    attachmentsCount(): number {
      return (this.examination.examinationAttachments?.length || 0) + this.pendingAttachments.length
    },
    notesCount(): number {
      return this.examinationNotes.length
    },
    customerDisplayName() {
      if (this.customer.id) {
        return `${this.customer.name}`
      }
      return ''
    },
    customerDateOfBirth() {
      if (this.customer.id && this.customer.dateOfBirth) {
        return `${formatPostgresDate(String(this.customer.dateOfBirth), 'DD/MM/YYYY')}`
      }
      return ''
    },
    customerAge() {
      if (this.customer.id && this.customer.dateOfBirth) {
        return `${formatAge(String(this.customer.dateOfBirth))}`
      }
      return ''
    },
    examinationCreatedDate() {
      if (this.examination.id) {
        return `${formatPostgresDate(String(this.examination.createdAt), 'DD/MM/YYYY')}`
      } else {
        return null
      }
    }
  },

  methods: {
    formatDate,
    formatNoteDateTime(date: any): string {
      return date ? dayjs(date).format('DD/MM/YYYY  hh:mm A') : ''
    },
    async fetchNotes() {
      if (!this.examination.id) return
      const res = await this.$http.get<ExaminationNote[]>(
        `${ExaminationNote.ENDPOINT}?examination_id=${this.examination.id}`
      )
      this.examinationNotes = res.map((n) => ExaminationNote.from(n))
    },
    openPinDialog() {
      if (!this.newNote.trim() || !this.examination.id) return
      this.notePin = ''
      if (useSettingsStore().getValue('is_cashier_pin_required') === 'true') {
        this.notePinDialogOpen = true
      } else {
        this.confirmPinAndSubmit()
      }
    },
    async confirmPinAndSubmit() {
      const text = this.newNote.trim()
      if (!text || !this.examination.id) return
      const headers: Record<string, string> = {}
      if (this.notePin) headers.pin = this.notePin
      try {
        const note = await this.$http.post<ExaminationNote>(
          `${ExaminationNote.ENDPOINT}?examination_id=${this.examination.id}`,
          { note: text },
          { headers }
        )
        this.notePinDialogOpen = false
        this.examinationNotes.push(ExaminationNote.from(note as any))
        this.newNote = ''
        this.notePin = ''
      } catch (err: any) {
        ElMessage.error(err?.title || err?.message || 'Error')
        this.notePin = ''
        this.$nextTick(() => (this.$refs.notePinInput as any)?.focus())
      }
    },
    startEditNote(note: ExaminationNote) {
      this.editingNoteID = note.id
      this.editingNoteText = note.note
    },
    cancelEdit() {
      this.editingNoteID = ''
      this.editingNoteText = ''
    },
    async saveNoteEdit(id: string) {
      const text = this.editingNoteText.trim()
      if (!text) return
      const updated = await this.$http.put<ExaminationNote>(
        `${ExaminationNote.ENDPOINT}/${id}`,
        { note: text }
      )
      const idx = this.examinationNotes.findIndex((n) => n.id === id)
      if (idx !== -1) this.examinationNotes[idx] = ExaminationNote.from(updated as any)
      this.editingNoteID = ''
      this.editingNoteText = ''
    },
    onCustomerCreater(customer: Customer) {
      this.customer = customer
      this.examination.customer = customer
      this.examination.customerID = customer.id
      this.newCustomerDialogVisiable = false
      this.customerSearchTerm = ''
    },
    openNewCustomer() {
      (this.$refs.customerSelect as { blur?: () => void } | undefined)?.blur?.()
      this.selectCustomerDialogVisiable = false
      this.newCustomerDialogVisiable = true
    },

    onAppointmentCreated(appointment: Appointment) {
      this.examination.appointment = appointment;
      this.examination.appointmentID = appointment.id;
      this.examination.nextAppointment = appointment.appointmentDate
      this.showAppointmentDialog = false
    },

    async searchCustomers(query: string) {
      if (!query) {
        query = ''
      }
      this.customerSearchTerm = query.trim()
      try {
        const url = `${Customer.ENDPOINT}?q=${query}`
        const customersRes = await this.$http.get<Customer[]>(url)
        this.customers = customersRes.map((customer) => Customer.from(customer))
      } catch (error) {
        this.$alertModal.showDanger({title: error.title})
        this.customers.value = []
      }
    },

    confirmSelectCustomer() {
      this.customer = this.selectedCustomerElSelect! as Customer
      this.examination.customer = this.selectedCustomerElSelect! as Customer
      this.examination.customerID = this.customer.id
      this.selectCustomerDialogVisiable = false
    },

    print() {
      this.$router.push({
        name: 'examination-receipt',
        params: {examination_id: this.examination.id}
      })
    },

    printID() {
      this.$router.push({
        name: 'examination-id-print',
        params: {examination_id: this.examination.id}
      })
    },

    async downloadQR() {
      const settingsStore = useSettingsStore()
      const logoSrc = settingsStore.getValue('examination_main_logo')
        ? settingsStore.storagePath + '/' + settingsStore.getValue('examination_main_logo')
        : samiLogo
      const qrUrl = `https://exams.thecky.com/${settingsStore.getValue('viewer_client_key') || 'test'}/${this.examination.id}`
      await exportQRCode({
        qrUrl,
        logoSrc,
        customerName: this.customer?.name,
        externalID: this.examination?.externalID,
        examDate: this.examination?.createdAt ? formatPostgresDate(String(this.examination.createdAt), 'DD-MM-YYYY') : '',
        brandColor: settingsStore.getValue('receipt_branding_color'),
        style: settingsStore.getValue('examination_qr_image_style')
      })
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      this.examination.notes = undefined

      this.examination.contactODAXIS = undefined
      this.examination.contactODCYL = undefined
      this.examination.contactODSPH = undefined
      this.examination.contactODK1 = undefined
      this.examination.contactODK2 = undefined
      this.examination.contactODKAVE = undefined
      this.examination.contactODBC = undefined
      this.examination.contactODDIA = undefined

      this.examination.contactOSAXIS = undefined
      this.examination.contactOSCYL = undefined
      this.examination.contactOSSPH = undefined
      this.examination.contactOSK1 = undefined
      this.examination.contactOSK2 = undefined
      this.examination.contactOSKAVE = undefined
      this.examination.contactOSBC = undefined
      this.examination.contactOSDIA = undefined

      this.examination.vaWithGlassesOD = undefined
      this.examination.vaWithGlassesOS = undefined
      this.examination.vaWithoutGlassesOD = undefined
      this.examination.vaWithoutGlassesOS = undefined
      this.examination.vaCatfordDrum = false

      this.examination.distanceODAXIS = undefined
      this.examination.distanceODCYL = undefined
      this.examination.distanceODIPD = undefined
      this.examination.distanceODSPH = undefined
      this.examination.distanceOSAXIS = undefined
      this.examination.distanceOSCYL = undefined
      this.examination.distanceOSSPH = undefined

      this.examination.readingODAXIS = undefined
      this.examination.readingODCYL = undefined
      this.examination.readingODIPD = undefined
      this.examination.readingODSPH = undefined
      this.examination.readingOSAXIS = undefined
      this.examination.readingOSSPH = undefined
      this.examination.readingOSCYL = undefined
      this.examination.iopOD = undefined
      this.examination.iopODStatus = undefined
      this.examination.iopOS = undefined
      this.examination.iopOSStatus = undefined

      this.examination.prismOD = undefined
      this.examination.prismODBASE = undefined
      this.examination.prismODAXIS = undefined

      this.examination.prismOS = undefined
      this.examination.prismOSBASE = undefined
      this.examination.prismOSAXIS = undefined

      this.examination.retinoscopy = emptyRetinoscopy()

      form?.resetFields()
    },

    // Request body for saving this exam: multipart on create, JSON on update
    buildSubmitPayload(extra: Partial<Examination> = {}, extraAttachments: File[] = []): Examination | FormData {
      const updatedObj = {...this.examination, ...extra} as Examination
      // One exam number for the visit: an Eye Exam carries the Retinoscopy filled on the same page
      if (updatedObj.examType !== EXAM_TYPE_RETINOSCOPY) {
        updatedObj.withRetinoscopy = retinoscopyIsFilled(updatedObj.retinoscopy)
      }

      if (updatedObj.examinator && updatedObj.examinatorID) {
        if (updatedObj.examinator.id != updatedObj.examinatorID) {
          updatedObj.examinator = undefined
        }
      }

      for (const key in updatedObj) {
        if (updatedObj.hasOwnProperty(key)) {
          const typedKey = key as keyof Examination
          if (updatedObj[typedKey] === undefined) {
            // @ts-ignore
            updatedObj[typedKey] = null
          }
        }
      }

      // Create always uses multipart (same as item form); update uses JSON
      let payload: Examination | FormData = updatedObj
      if (!updatedObj.id) {
        const formData = new FormData()
        formData.append('body', JSON.stringify(updatedObj))
        ;[...this.pendingAttachments, ...extraAttachments].forEach(f => formData.append('attachments', f))
        payload = formData
      }
      return payload
    },
    async validateSubmit(formName: string, print = false) {
      if (this.isSubmitting || this.premiumLocked) {
        return false
      }

      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        if (!this.examination.customerID) {
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2>${this.t('pleaseSelectACustomer')}</h2>`
          })
          return false
        }

        const retinoscopy = this.examination.retinoscopy
        if (!this.retinoscopyLocked && retinoscopy
            && (retinoscopyMissingWorkingDistance(retinoscopy.dryRetinoscopy) || retinoscopyMissingWorkingDistance(retinoscopy.cycloplegicRetinoscopy))) {
          this.retinoscopyExpanded = true
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2 dir="auto">${this.t('workingDistanceRequired')}</h2>`
          })
          return false
        }

        if (!this.examination.examinatorID) {
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2>${this.t('pleaseSelectAnOptometrist')}</h2>`
          })
          return false
        }

        this.isSubmitting = true
        try {
          const payload = this.buildSubmitPayload()

          const resp = await this.submitAction(payload, print)

          if (resp) {
            this.error.title = resp?.title
            this.error.body = resp?.body
            document.getElementById('main')?.scrollTo({top: 0})
            return false
          } else {
            this.pendingAttachments = []
            if (this.clearFormOnSubmit) {
              this.clearForm(formName)
            } else {
              await this.fetchNotes()
            }

            this.$alertModal.showSuccess({title: this.successMsg, body: ''})
          }
          return true
        } finally {
          this.isSubmitting = false
        }
      })
    },
    async onExaminationSelected(examination: Examination) {
      if (examination.id && examination.id === this.examination.id) return
      // A saved exam opens the picked exam; a new one copies an old Eye Exam into the form (without its Retinoscopy)
      if (examination.id && (this.examination.id || examination.examType === EXAM_TYPE_RETINOSCOPY)) {
        if (!this.examination.id && this.customer.id && this.$route.query.customer_id !== this.customer.id) {
          await this.$router.replace({ query: { ...this.$route.query, customer_id: this.customer.id } })
        }
        await this.$router.push({ name: 'examination', params: { examination_id: examination.id } })
        return
      }
      // equal all except id, createdAt, updatedAt, deletedAt, appointmentID, nextAppointment, isStarred, appointment, and externalID
      this.examination = {...examination, id: undefined, createdAt: undefined, updatedAt: undefined, deletedAt: undefined, appointmentID: undefined, nextAppointment: undefined, isStarred: undefined, appointment: undefined, externalID: undefined, doctor: "", examinatorID: undefined, examinator: undefined, examType: this.examination.examType, withRetinoscopy: false, retinoscopy: emptyRetinoscopy()}
    },
    clearCustomer() {
      this.customer = new Customer()
      this.examination.customerID = undefined
      this.examination.customer = undefined
    },
    addCustomer(customer: Customer) {
      this.customer = customer
      this.examination.customerID = customer.id
      this.examination.customer = customer
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3 font-bold;
}

formt > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}

.premium-content {
  @apply w-full flex flex-col items-center;
}

.premium-locked {
  @apply select-none;
  opacity: .55;
  filter: grayscale(.4);
}

.premium-banner {
  @apply w-full flex items-center gap-3 rounded-lg mb-3;
  padding: 12px 16px;
  background: linear-gradient(100deg, #fff8e6 0%, #fdf1cf 100%);
  border: 1px solid #f1d28a;
  color: #6b4a00;
}

.premium-banner-icon {
  @apply flex items-center justify-center shrink-0 rounded-full;
  width: 38px;
  height: 38px;
  background: #f5b82e;
  color: #fff;
  box-shadow: 0 2px 6px rgba(245, 184, 46, .45);
}

.premium-banner-title {
  @apply font-bold text-base;
}

.premium-banner-body {
  @apply text-sm;
  color: #7c5a12;
}

.premium-banner-lock {
  @apply flex shrink-0;
  margin-inline-start: auto;
  color: #b8860b;
}

.custom-grid-examination-notes {
  grid-template-columns: 145px 1fr;
}

.dir-rtl {
  direction: rtl;
}

.exam-layout {
  @apply flex flex-col gap-4 w-full px-2 mx-auto;
  max-width: 1180px;
}

.exam-card {
  @apply rounded-lg border border-gray-200 bg-white px-4 py-3;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.exam-card :deep(textarea) {
  unicode-bidi: plaintext;
}

.exam-card-title {
  @apply font-bold mb-2;
  border-inline-start: 3px solid var(--el-color-primary);
  padding-inline-start: 0.6rem;
}

.retino-toggle {
  @apply flex items-center justify-between gap-3 w-full;
  text-align: start;
  cursor: pointer;
}

.retino-toggle:disabled {
  cursor: default;
}

.retino-heading {
  @apply flex-1;
  border-inline-start: 3px solid #b8860b;
  padding-inline-start: 0.6rem;
}

.retino-abbr {
  @apply text-xs font-bold ms-1;
  color: #b8860b;
}

.retino-subtitle {
  @apply text-sm text-gray-500;
}

.retino-lock {
  color: #b8860b;
}

.retino-locked-note {
  @apply text-sm rounded-md px-3 py-2 mb-3;
  color: #7a5a00;
  background: #fdf6e3;
  border: 1px solid #f0dca4;
}

.retino-locked {
  opacity: 0.75;
}

.patient-row {
  @apply flex flex-wrap items-center gap-x-6 gap-y-3;
}

.patient-identity {
  @apply flex items-center gap-3 min-w-0;
  flex: 1 1 280px;
}

.patient-avatar {
  @apply flex items-center justify-center shrink-0 rounded-full;
  width: 48px;
  height: 48px;
  color: #1e4f7b;
  background: #dbecfc;
  border: 1px solid #b6d4f2;
}

.patient-main {
  @apply flex flex-col min-w-0;
}

.patient-label {
  @apply text-xs text-gray-500;
}

.patient-name-row {
  @apply flex items-start gap-2 min-w-0;
}

.patient-name {
  @apply text-lg font-bold leading-snug text-start rounded;
  color: var(--primary-bg-color, #1D4C75);
  unicode-bidi: plaintext;
  overflow-wrap: anywhere;
}

.patient-name:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.patient-clear {
  @apply flex items-center justify-center shrink-0 rounded-full text-gray-400;
  width: 24px;
  height: 24px;
  /* centre on the first line's letters, which sit above the middle of Tajawal's line box */
  margin-top: -3px;
}

.patient-clear:hover {
  @apply text-red-600 bg-red-50;
}

.patient-phone {
  @apply text-sm text-gray-500;
  text-align: start;
}

.patient-empty {
  @apply flex items-center gap-3 rounded-lg px-4 py-2 text-start;
  flex: 1 1 280px;
  border: 1.5px dashed #b6d4f2;
  background: #f5faff;
  color: #1e4f7b;
}

.patient-empty:focus,
.patient-name:focus,
.patient-stat:focus {
  outline: none;
}

.patient-empty:focus-visible,
.patient-name:focus-visible {
  outline: 2px solid #b6d4f2;
  outline-offset: 2px;
}

.patient-empty:hover {
  background: #e9f3fe;
  border-color: var(--primary-bg-color, #1D4C75);
}

.patient-empty-icon {
  @apply flex items-center justify-center rounded-full text-white shrink-0;
  width: 36px;
  height: 36px;
  background: var(--primary-bg-color, #1D4C75);
}

.patient-stats {
  @apply flex flex-wrap items-stretch gap-2;
}

.patient-stat {
  @apply flex flex-col items-start rounded-lg px-3 py-1.5 bg-gray-50 border border-gray-200;
  min-width: 96px;
}

button.patient-stat:not(:disabled):hover {
  @apply border-gray-300 bg-gray-100;
}

.patient-stat-value {
  @apply font-bold text-gray-800;
  font-variant-numeric: tabular-nums;
}

.patient-tools {
  @apply flex items-center gap-2;
  margin-inline-start: auto;
}

.care-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 0.75rem 1rem;
}

.care-field {
  @apply flex flex-col gap-1 min-w-0;
}

.care-label {
  @apply text-sm text-gray-600;
}

.care-appt {
  @apply flex items-center gap-2 w-full rounded px-3 text-sm text-start;
  height: 32px;
  border: 1px dashed #b6d4f2;
  color: #1e4f7b;
  background: #f5faff;
}

.care-appt:hover {
  border-color: var(--primary-bg-color, #1D4C75);
}

.care-appt-set {
  @apply font-bold;
  border-style: solid;
  background: #dbecfc;
}

.custom-required:before {
  content: '*';
  color: var(--el-color-danger);
  margin-right: 4px;
}
/* Phones only: desktop layout above is unchanged */
@media (max-width: 767px) {
  .examination-form {
    margin: 0.5rem !important;
    padding: 0.5rem !important;
  }

  .exam-layout {
    @apply gap-3 px-0;
  }

  .exam-card {
    @apply px-3;
  }

  .patient-row {
    @apply gap-3;
  }

  .patient-identity,
  .patient-empty {
    flex-basis: 100%;
  }

  .patient-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .patient-stat {
    min-width: 0;
  }

  .patient-tools {
    @apply w-full justify-end;
    margin-inline-start: 0;
  }

  .care-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .input-section > div {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    padding-top: 0.75rem;
  }

  .input-section > div > div {
    margin: 0;
  }

  .input-section :deep(.button) {
    min-height: 40px;
  }
}
.add-typed-customer {
  @apply flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-start;
  color: var(--el-color-primary);
}

.add-typed-customer:hover {
  background: var(--el-fill-color-light);
}
</style>
